import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { Store } from '@ngxs/store';
import { DmAlert, DmSidebarInset, DmSidebarMobileHeader, DmSidebarProvider } from '@digitale-menschen/component-library-angular';
import { injectMobileSidebarCloser, LexplusLogoComponent, ProfileState, ProfileStateUpdatePasswordExpiration } from '@lexplus-shared-lib';
import dayjs from 'dayjs';
import { FaqDrawerComponent } from '../../../features/faq/components/faq-drawer/faq-drawer.component';
import { KontaktModalComponent } from '../kontakt-modal/kontakt-modal.component';
import { SidebarNavigationComponent } from '../sidebar-navigation/sidebar-navigation.component';

// Mirrors dm-sidebar-provider's own (undocumented internal) cookie name, read
// synchronously so our collapsed-state styling starts correct on first render.
const SIDEBAR_STATE_COOKIE_NAME = 'dm_sidebar_state';

function readInitialSidebarOpenState(): boolean {
  const match = document.cookie.match(new RegExp(`(?:^|; )${SIDEBAR_STATE_COOKIE_NAME}=([^;]*)`));
  return match ? match[1] === 'true' : true;
}

/**
 * Layout route around every page of the signed-in app: sidebar, mobile header, password
 * banner, FAQ drawer and contact modal. The router swaps it against the auth layout in a
 * single activation, so a login or logout never shows one layout inside the other.
 */
@Component({
  selector: 'lp-app-shell',
  imports: [
    RouterOutlet,
    DmAlert,
    DmSidebarProvider,
    DmSidebarInset,
    DmSidebarMobileHeader,
    LexplusLogoComponent,
    SidebarNavigationComponent,
    FaqDrawerComponent,
    KontaktModalComponent,
  ],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.scss',
})
export class AppShellComponent {
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  private readonly sidebarProvider = viewChild<DmSidebarProvider>('sidebarProvider');

  protected readonly sidebarExpanded = signal(readInitialSidebarOpenState());
  protected readonly faqOpen = signal<boolean>(false);
  protected readonly kontaktOpen = signal<boolean>(false);

  private readonly passwordExpiration = this.store.selectSignal(ProfileState.passwordExpiration);

  protected readonly showPasswordExpirationWarning = computed<boolean>(() => this.passwordExpiration()?.showBanner ?? false);

  protected readonly passwordExpirationRemainingDays = computed<number>(() => {
    const expiration = this.passwordExpiration();
    if (!expiration?.showBanner) {
      return 0;
    }
    return Math.ceil(dayjs(expiration.passwordExpirationDate).diff(dayjs(), 'day', true));
  });

  // Held for its route-change subscription, which closes the mobile drawer after
  // navigation. The returned `close()` is unused: unlike the admin sidebar, this
  // app's navigation has no non-navigating interaction to close the drawer for.
  private readonly mobileSidebarCloser = injectMobileSidebarCloser(this.sidebarProvider);

  protected openContact(): void {
    this.faqOpen.set(false);
    this.kontaktOpen.set(true);
  }

  protected passwordAlertClosed(): void {
    this.store.dispatch(new ProfileStateUpdatePasswordExpiration(false));
  }

  protected async goToProfilePassword(): Promise<void> {
    // todo: go to the future Profile-Page / Password-change
    await this.router.navigateByUrl('/profil/password');
  }
}
